/** One run of a word diff: kept, added in the newer text, or removed from the older. */
export interface IDiffPart {
  kind: 'same' | 'added' | 'removed';
  text: string;
}

/** Words, the spaces between them, and a placeholder as one word. */
const TOKENS = /\{\{[^{}]*\}\}|\s+|[^\s{]+|\{/g;

/**
 * A word-level diff of two template fields — a longest common subsequence over the tokens, which
 * at a field's 4000 characters stays well under a million cells. Adjacent runs of one kind merge.
 */
export function wordDiff(before: string, after: string): IDiffPart[] {
  const a = before.match(TOKENS) ?? [];
  const b = after.match(TOKENS) ?? [];
  const width = b.length + 1;
  // lengths[i * width + j]: the LCS of a[i..] and b[j..].
  const lengths = new Uint32Array((a.length + 1) * width);
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lengths[i * width + j] =
        a[i] === b[j]
          ? lengths[(i + 1) * width + j + 1] + 1
          : Math.max(lengths[(i + 1) * width + j], lengths[i * width + j + 1]);
    }
  }
  const parts: IDiffPart[] = [];
  const push = (kind: IDiffPart['kind'], text: string) => {
    const last = parts[parts.length - 1];
    if (last?.kind === kind) last.text += text;
    else parts.push({ kind, text });
  };
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      push('same', a[i]);
      i++;
      j++;
    } else if (lengths[(i + 1) * width + j] >= lengths[i * width + j + 1]) {
      push('removed', a[i++]);
    } else {
      push('added', b[j++]);
    }
  }
  while (i < a.length) push('removed', a[i++]);
  while (j < b.length) push('added', b[j++]);
  return parts;
}
